import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
  styled,
  lighten,
  Stack,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PricingPlanFeatureGroup } from "../PricingPlanFeatureGroup";
import {
  BORDER_COLORS,
  BUTTON_COLORS,
  FONT_COLORS,
} from "../../../../constants";
import { formatCurrency } from "../../../../helpers";
import { TPricingPlanItem } from "../../../../features/pricingPlans/pricingPlans.types";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { useAppSelector } from "../../../../store/hooks";
import { selectUserProfile } from "../../../../features/user/user.selector";

type TPricingPlanItemProps = {
  plan: TPricingPlanItem;
  onPlanChange: (planId: string) => Promise<void>;
  currentPlanId?: string;
  isLoading?: boolean;
};

const StyledContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  border: `1px solid ${lighten(BORDER_COLORS.default, 0.3)}`,
}));

const StyledButton = styled(Button)(() => ({
  height: 42,
  fontSize: 14,
  fontWeight: 600,
  textTransform: "uppercase",
  backgroundColor: BUTTON_COLORS.primary,
}));

const PLAN_RANK: Record<string, number> = {
  basic_monthly: 0,
  basic_annually: 1,
  growth_monthly: 2,
  growth_annually: 3,
};

export const PricingPlanItem = ({
  plan,
  currentPlanId,
  onPlanChange,
  isLoading,
}: TPricingPlanItemProps) => {
  const { trackPlanSelected } = useAnalytics();
  const user = useAppSelector(selectUserProfile);
  const subscriptionState = user?.subscriptionState;
  const onboardingState = user?.onboardingState;

  const isCurrentPlan = currentPlanId === plan.planId;
  const isGrowth = plan.name.toLowerCase().split(" ")[0] === "growth";

  const lookups =
    plan.features.productAnalyzer.lookups === -1
      ? "Unlimited Lookups"
      : `${plan.features.productAnalyzer.lookups.toLocaleString()} Lookups included`;

  const billedYearly =
    plan.period === "annually"
      ? `$${(Math.floor(plan.price * 12 * 100) / 100).toFixed(2)} `
      : null;

  const handlePlanClick = async () => {
    if (plan.onChange) {
      plan.onChange();
    } else {
      await onPlanChange(plan.id);
    }
    trackPlanSelected({
      planId: plan.id,
      planName: plan.name,
      planPrice: plan.price,
      planDuration: plan.period || undefined,
    });
  };

  const getActionLabel = (): string => {
    if (isCurrentPlan) return "Current Plan";
    if (onboardingState === "pending_plan" && subscriptionState === null)
      return "Start Free Trial";
    const currentRank = currentPlanId ? (PLAN_RANK[currentPlanId] ?? -1) : -1;
    const thisRank = PLAN_RANK[plan.planId] ?? -1;
    return thisRank > currentRank ? "Upgrade" : "Downgrade";
  };

  const actionLabel = getActionLabel();

  const isDowngradeScheduled =
    !isCurrentPlan && user?.planDetails?.nextPlanId === plan.planId;

  const scheduledDate =
    isDowngradeScheduled && user?.planDetails?.nextPaymentDate
      ? new Date(user.planDetails.nextPaymentDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : null;

  const DowngradeScheduledMessage =
    isDowngradeScheduled && scheduledDate ? (
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        mt={1.5}
        px={1.5}
        py={1}
        sx={{
          backgroundColor: "#E9FBEFCC",
          borderRadius: "8px",
          border: "1px solid #0476474D",
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 16, color: "#047647", flexShrink: 0 }}
        />
        <Typography
          fontSize="11px"
          fontWeight={500}
          color="#047647"
          lineHeight="14px"
        >
          Your downgrade is scheduled. Your plan will switch to{" "}
          <Box component="span" fontWeight={600} textTransform="capitalize">
            {plan.name.split(" ")[0]}
          </Box>{" "}
          on{" "}
          <Box component="span" fontWeight={600}>
            {scheduledDate}
          </Box>
        </Typography>
      </Stack>
    ) : null;

  return (
    <>
      {plan.name !== "free" && (
        <StyledContainer
          elevation={0}
          sx={{
            width: { xs: "100%", sm: "314px" },
            position: "relative",
            mt: { xs: isGrowth ? "16px" : 0, sm: 0 },
            border: {
              xs: `2px solid ${isCurrentPlan ? "#0060FF" : "#E5E7EB"}`,
              sm: `1px solid ${lighten(BORDER_COLORS.default, 0.3)}`,
            },
            borderRadius: { xs: "16px", sm: "4px" },
            boxShadow: {
              xs: "0px 2px 8px rgba(0,0,0,0.06)",
              sm: "none",
            },
          }}
        >
          {/* RECOMMENDED badge - xs only */}
          {isGrowth && (
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                position: "absolute",
                top: 0,
                right: 0,
                background:
                  "linear-gradient(180deg, #0060FF 10.85%, #1E3A8A 87.97%)",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 700,
                px: 1.5,
                py: 0.5,
                borderRadius: "0px 12px 0 12px",
                alignItems: "center",
                gap: "4px",
                whiteSpace: "nowrap",
                boxShadow: "0px 4px 8px 0px #00000017",
                zIndex: 1,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 12 }} />
              RECOMMENDED
            </Box>
          )}

          {/* xs content */}
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={0.5}
            >
              <Typography
                fontSize="20px"
                fontWeight={700}
                color="#171A1F"
                textTransform="capitalize"
              >
                {plan.name.split(" ")[0]}
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.3}>
                <Typography fontSize="24px" fontWeight={900} color="#171A1F">
                  {formatCurrency(
                    plan.period === "annually"
                      ? Math.floor(plan.price * 100) / 100
                      : plan.price,
                  )}
                </Typography>
                <Typography fontSize="12px" color="#565D6D" fontWeight={700}>
                  /MO
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <Typography fontSize="14px" fontWeight={500} color="#565D6D">
                {lookups}
              </Typography>
              {billedYearly && (
                <Typography fontSize="11px" fontWeight={500}>
                  <Box component="span" color="#565D6D">
                    {billedYearly}
                  </Box>
                  <Box component="span" color="#171A1F">
                    Billed yearly
                  </Box>
                </Typography>
              )}
            </Stack>

            <Typography fontSize="12px" color="#565D6D" fontWeight={500} mb={2}>
              Full feature access
            </Typography>

            <Button
              fullWidth
              variant={isCurrentPlan ? "contained" : "outlined"}
              disabled={isCurrentPlan}
              loading={isLoading}
              onClick={handlePlanClick}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "13px",
                borderRadius: "18px",
                py: 0.8,
                minHeight: "36px",
                ...(isCurrentPlan
                  ? {
                      background:
                        "linear-gradient(180deg, #0060FF 10.85%, #1E3A8A 87.97%)",
                      boxShadow: "0px 4px 8px 0px #00000017",
                      color: "#FFFFFF",
                      "&.Mui-disabled": {
                        background:
                          "linear-gradient(180deg, #0060FF 10.85%, #1E3A8A 87.97%)",
                        color: "#FFFFFF",
                      },
                    }
                  : { borderColor: "#DEE1E6", color: "#171A1F" }),
              }}
            >
              {actionLabel}
            </Button>
            {DowngradeScheduledMessage}
          </Box>

          {/* sm+ content — unchanged */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Stack flexDirection="row" justifyContent="space-between">
              <Box>
                <Typography
                  textAlign="left"
                  fontSize={20}
                  fontWeight={500}
                  textTransform="capitalize"
                  color={FONT_COLORS.default}
                >
                  {plan.name}
                </Typography>
                <Typography
                  mt={2}
                  textAlign="left"
                  fontSize={30}
                  fontWeight={700}
                  color={FONT_COLORS.default}
                >
                  {formatCurrency(
                    plan.period === "annually"
                      ? Math.floor(plan.price * 100) / 100
                      : plan.price,
                  )}
                  <Typography
                    textAlign="left"
                    component="span"
                    fontSize={16}
                    fontWeight={500}
                    color={FONT_COLORS.default}
                  >
                    /Month
                  </Typography>
                </Typography>
                <Typography
                  mt={1}
                  mb={2.5}
                  textAlign="left"
                  fontSize={14}
                  fontWeight={500}
                  color={lighten(FONT_COLORS.default, 0.4)}
                >
                  {plan.period === null
                    ? "-"
                    : plan.period === "monthly"
                      ? "Billed Monthly"
                      : "Billed Annually"}
                </Typography>
              </Box>
            </Stack>
            <StyledButton
              fullWidth
              disabled={currentPlanId === plan.planId}
              loading={isLoading}
              variant="contained"
              onClick={handlePlanClick}
            >
              {actionLabel}
            </StyledButton>
            {DowngradeScheduledMessage}
            <Divider sx={{ my: 2 }} />
            <PricingPlanFeatureGroup features={plan.features} />
          </Box>

          {/* MOST POPULAR ribbon - sm+ only */}
          {plan.name === "growth" && (
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
                position: "absolute",
                top: 0,
                right: 0,
                width: "120px",
                height: "120px",
                overflow: "hidden",
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 15,
                  right: -70,
                  width: "200px",
                  backgroundColor: "#2563EB",
                  color: "white",
                  textAlign: "center",
                  fontSize: 9,
                  fontWeight: 600,
                  lineHeight: "30px",
                  transform: "rotate(45deg)",
                  boxShadow: "0px 4px 10px rgba(21, 94, 239, 0.35)",
                }}
              >
                MOST POPULAR
              </Box>
            </Box>
          )}
        </StyledContainer>
      )}
    </>
  );
};
